var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { fetchPeriodicalLibraries, } from "../api/periodicalLibraryService";
import { PeriodicalLibraryStatusEnum } from "../types/Api";
import { DEFAULT_PAGE_SIZE } from "../utils/pageSize";
import { usePageSize } from "./usePageSize";
import { DEFAULT_TITLE_SORT } from "../utils/titleSort";
import { isCountCapped } from "../utils/exactCount";
import { useExactTotalCount } from "./useExactTotalCount";
import { periodicalStatusParams } from "../utils/volumeConstants";
import { toLastReceivedQueryParams } from "../utils/lastReceivedFilter";
import { retryUnlessClientError } from "../utils/apiUtils";
var buildParams = function (filters) {
    var name = filters.name.trim();
    var issn = filters.issn.trim();
    var publisherId = filters.publisherId.trim();
    var frequencies = filters.frequencies;
    var _a = periodicalStatusParams(filters.statuses), depositStatuses = _a.depositStatuses, enforcementTypes = _a.enforcementTypes;
    return __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, (name && { name: name })), (issn && { issn: issn })), (publisherId && { publisherId: publisherId })), (frequencies.length > 0 && { frequencies: frequencies })), (filters.kultSubjectGroupOrigins.length > 0 && {
        kultSubjectGroupOrigins: filters.kultSubjectGroupOrigins,
    })), (depositStatuses.length > 0 && { depositStatuses: depositStatuses })), (enforcementTypes.length > 0 && { enforcementTypes: enforcementTypes })), (filters.provisional && { provisional: true })), (filters.agendaState && { agendaState: filters.agendaState })), toLastReceivedQueryParams(filters)), { activeOnly: !filters.showEnded });
};
export var useSubscribedPeriodicalsData = function (appliedFilters, sort) {
    var _a, _b;
    if (sort === void 0) { sort = DEFAULT_TITLE_SORT; }
    var kyInstance = useOkapiKy();
    var _c = useState(0), pageOffset = _c[0], setPageOffset = _c[1];
    var _d = usePageSize(), pageSize = _d.pageSize, setPageSize = _d.setPageSize;
    useEffect(function () {
        setPageOffset(0);
    }, [appliedFilters, sort]);
    var _e = useQuery([
        "periodicalLibraries",
        "SUBSCRIBED",
        appliedFilters,
        sort,
        pageOffset,
        pageSize,
    ], function () {
        return fetchPeriodicalLibraries(kyInstance, __assign(__assign({ status: PeriodicalLibraryStatusEnum.SUBSCRIBED }, buildParams(appliedFilters)), { sortBy: sort.sortBy, sortDir: sort.sortDir, offset: pageOffset, limit: pageSize }));
    }, {
        keepPreviousData: true,
        retry: retryUnlessClientError,
    }), data = _e.data, isLoading = _e.isLoading, isFetching = _e.isFetching, isError = _e.isError, error = _e.error, refetch = _e.refetch;
    var periodicalsData = isError ? [] : ((_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : []);
    var totalRecordsExact = data === null || data === void 0 ? void 0 : data.totalRecordsExact;
    var exactTotalRecords = useExactTotalCount({
        queryKey: ["periodicalLibraries", "SUBSCRIBED", appliedFilters],
        fetchTotalRecords: function () { return __awaiter(void 0, void 0, void 0, function () {
            var response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, fetchPeriodicalLibraries(kyInstance, __assign(__assign({ status: PeriodicalLibraryStatusEnum.SUBSCRIBED }, buildParams(appliedFilters)), { offset: 0, limit: DEFAULT_PAGE_SIZE, exactCount: true }))];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response.totalRecords];
                }
            });
        }); },
        totalRecordsExact: totalRecordsExact,
    });
    var totalRecords = isError
        ? 0
        : ((_b = exactTotalRecords !== null && exactTotalRecords !== void 0 ? exactTotalRecords : data === null || data === void 0 ? void 0 : data.totalRecords) !== null && _b !== void 0 ? _b : 0);
    var fetchMoreData = useCallback(function (offset) { return setPageOffset(offset); }, []);
    var changePageSize = useCallback(function (size) {
        setPageSize(size);
        setPageOffset(0);
    }, [setPageSize, setPageOffset]);
    return {
        periodicalsData: periodicalsData,
        totalRecords: totalRecords,
        exactTotalRecords: exactTotalRecords,
        isCountCapped: isCountCapped(totalRecordsExact, exactTotalRecords),
        isLoading: isLoading,
        isFetching: isFetching,
        isError: isError,
        error: error,
        refetch: refetch,
        fetchMoreData: fetchMoreData,
        pageOffset: pageOffset,
        pageSize: pageSize,
        changePageSize: changePageSize,
    };
};
